import { Suspense, lazy, useEffect, useState } from 'react';

import { isPrerender } from '../lib/default-brand.js';

/**
 * Hero background gate (Task 1). Renders the static poster by default (the
 * hero's CSS `--hero-ground` + `.hero::before` mesh gradient — always present,
 * including under prerender / no-JS) and only *mounts* the code-split WebGL
 * scene (`hero-3d.tsx`) after first paint AND when the device can clearly
 * afford it. `React.lazy` keeps `three`/fiber/drei out of the initial +
 * prerender bundle; the dynamic import only fires once `enabled` flips true, so
 * the prerender browser (where `isPrerender()` is true) never loads it.
 *
 * The 3D is decorative (`aria-hidden`) — a background enhancement layered
 * behind the real, prerendered hero headline/CTAs, which are untouched.
 */

const Hero3D = lazy(() => import('./hero-3d.js'));

interface NavigatorWithMemory extends Navigator {
  /** Non-standard but widely shipped (Chrome/Android); GiB of RAM, rounded. */
  readonly deviceMemory?: number;
  /**
   * Network Information API — Chrome/Android, i.e. most of this market.
   *
   * `saveData` is the user having ticked "Data Saver" in their browser, and
   * `effectiveType` is the connection quality the browser has measured.
   */
  readonly connection?: {
    readonly saveData?: boolean;
    readonly effectiveType?: string;
  };
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function hasWebGL(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
    return gl !== null;
  } catch {
    return false;
  }
}

/**
 * Keep the (cheap, valid) poster for constrained devices — protects SEA users
 * on slow phones / metered data from downloading + running the 3D bundle.
 */
function isLowEndDevice(): boolean {
  const nav = window.navigator as NavigatorWithMemory;
  const cores = nav.hardwareConcurrency;
  const memory = nav.deviceMemory;
  if (typeof cores === 'number' && cores <= 4) return true;
  if (typeof memory === 'number' && memory <= 4) return true;
  return false;
}

/**
 * The device is capable but the CONNECTION says don't.
 *
 * The capability gate above asks whether the phone can run the scene; it never
 * asked what downloading it would cost. The chunk is 876 KB, and this product
 * launches in a market where mobile data is metered and often slow — so a
 * flagship phone on a 3G connection, or one whose owner has explicitly asked
 * their browser to save data, was still fetching most of a megabyte for a
 * decorative background behind text that had already painted.
 *
 * `saveData` is the strongest possible signal: the user asked, in their browser
 * settings, for exactly this not to happen.
 */
function isExpensiveConnection(): boolean {
  const conn = (window.navigator as NavigatorWithMemory).connection;
  if (!conn) return false;
  if (conn.saveData === true) return true;
  const type = conn.effectiveType;
  return type === 'slow-2g' || type === '2g' || type === '3g';
}

/**
 * The 3D hero is a DESKTOP flourish, and this decides that explicitly.
 *
 * The capability gate below asks about cores and memory, which a modern phone
 * passes easily — an 8-core handset was being handed an 876 KB WebGL bundle
 * that costs ~7 s of script evaluation (measured 2026-08-23, Lighthouse
 * bootup-time). On a phone that is the difference between a fast page and a
 * janky one, and in a market where mobile data is metered it is also somebody's
 * money, spent on a decorative background behind text they can already read.
 *
 * A coarse pointer or a narrow viewport means a phone or small tablet. The
 * static CSS poster is the intended experience there, not a degraded one.
 */
function isSmallScreenOrTouch(): boolean {
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(max-width: 900px), (pointer: coarse)').matches;
}

function canRender3D(): boolean {
  if (typeof window === 'undefined' || isPrerender()) return false;
  if (prefersReducedMotion()) return false;
  if (isSmallScreenOrTouch()) return false;
  if (isLowEndDevice()) return false;
  if (isExpensiveConnection()) return false;
  return hasWebGL();
}

export function HeroScene(): JSX.Element {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (!canRender3D()) return;

    let idleId: number | undefined;
    let timeoutId: number | undefined;
    let cancelled = false;

    /**
     * Wait for genuine idle — with NO forced timeout.
     *
     * This used to be `requestIdleCallback(…, { timeout: 1200 })`, which reads
     * like "defer to idle" and is not: the timeout makes the browser run the
     * callback whether or not it is idle, so on a slow page — exactly the case
     * this guard exists for — the 876 KB WebGL chunk started downloading 1.2s
     * in, competing with the render it was supposed to follow. Measured
     * 2026-08-23: it was the single largest script fetched on the home page.
     *
     * Dropping the timeout means a permanently busy page never starts the
     * scene, and that is the correct outcome: the hero has a real CSS poster
     * behind it, the 3D is decorative and `aria-hidden`, and a visitor whose
     * device never gets a spare moment is precisely who should not be handed
     * most of a megabyte of Three.js.
     */
    const scheduleIdle = (): void => {
      if (cancelled) return;
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(() => {
          if (!cancelled) setEnabled(true);
        });
        return;
      }
      // Safari has no requestIdleCallback. A plain delay is the honest
      // approximation; it is generous on purpose so it lands well after paint.
      timeoutId = window.setTimeout(() => {
        if (!cancelled) setEnabled(true);
      }, 2000);
    };

    // Only start looking for idle once the page has finished loading, so the
    // scene can never compete with the critical render for bandwidth.
    if (document.readyState === 'complete') {
      scheduleIdle();
    } else {
      window.addEventListener('load', scheduleIdle, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('load', scheduleIdle);
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className="hero-scene" aria-hidden="true">
      {enabled ? (
        <Suspense fallback={null}>
          <Hero3D />
        </Suspense>
      ) : null}
    </div>
  );
}
